import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Video, RefreshCw, Play, Pause } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook to fetch fresh playback URL with auto-refresh
 */
const usePlaybackUrl = (videoType) => {
  const [data, setData] = useState({ url: null, posterUrl: null, hasVideo: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const refreshTimerRef = useRef(null);

  const fetchPlaybackUrl = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) setLoading(true);
      setError(null);
      
      const res = await fetch(`${API}/re-videos/playback/${videoType}`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      const result = await res.json();
      setData({
        url: result.url,
        posterUrl: result.posterUrl,
        hasVideo: result.hasVideo,
        expiresAt: result.expiresAt
      });
      
      // Schedule refresh before expiry (refresh 2 minutes before expiry)
      if (result.expiresAt) {
        const expiresAt = new Date(result.expiresAt).getTime();
        const refreshIn = Math.max(expiresAt - Date.now() - 120000, 60000); // At least 1 min
        
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
          console.log(`Refreshing ${videoType} playback URL`);
          fetchPlaybackUrl(true);
        }, refreshIn);
      }
      
      setRetryCount(0);
    } catch (err) {
      console.error(`Failed to fetch ${videoType} playback URL:`, err.message);
      setError(err.message);
      
      // Auto-retry once after 2 seconds
      if (retryCount < 1) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchPlaybackUrl(true);
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [videoType, retryCount]);

  useEffect(() => {
    fetchPlaybackUrl();
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [fetchPlaybackUrl]);

  const retry = useCallback(() => {
    setRetryCount(0);
    fetchPlaybackUrl();
  }, [fetchPlaybackUrl]);

  return { ...data, loading, error, retry };
};


/**
 * Hero Video Player for Real Estate page
 * - On load: autoplay muted, loop, no controls, only "Tap for sound" button
 * - After tap: restart from 0:00, unmute, show custom controls (Play/Pause + scrubber)
 * - Custom controls auto-hide after 3 seconds, reset on interaction
 * - Custom controls: No native browser controls, no 10s skip, no fullscreen, no PiP
 */
export const REHeroVideoPlayer = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [videoError, setVideoError] = useState(false);
  
  // Track if sound has been enabled (unlocks custom controls)
  const [soundEnabled, setSoundEnabled] = useState(false);
  // Track if custom controls are visible (auto-hide after 3 seconds)
  const [controlsVisible, setControlsVisible] = useState(false);
  // Ref for the hide timeout (so we can clear it on new interaction)
  const hideControlsTimeoutRef = useRef(null);
  // Track previous URL to detect src changes
  const prevUrlRef = useRef(null);
  
  // Custom controls state
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  
  // Fetch playback URL
  const { url, posterUrl, hasVideo, loading, error, retry } = usePlaybackUrl('hero');

  /**
   * Schedule controls to hide after 3 seconds
   * Clears any existing timeout first
   */
  const scheduleHideControls = useCallback(() => {
    // Clear existing timeout if any
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    // Schedule new hide after 3 seconds
    hideControlsTimeoutRef.current = setTimeout(() => {
      console.log('[RE-HERO] Auto-hiding controls after 3s');
      setControlsVisible(false);
    }, 3000);
  }, []);

  /**
   * Show controls and start/reset the 3-second hide timer
   */
  const showControls = useCallback(() => {
    setControlsVisible(true);
    scheduleHideControls();
  }, [scheduleHideControls]);

  /**
   * Hide controls immediately (used internally)
   */
  const hideControls = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    setControlsVisible(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Force muted state on mount and whenever src changes
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Detect src change - reset the sound state
    if (prevUrlRef.current !== url && url) {
      console.log('[RE-HERO] URL changed - resetting sound state');
      setSoundEnabled(false);
      setControlsVisible(false);
      setIsPlaying(true);
      setCurrentTime(0);
      hideControls();
    }
    prevUrlRef.current = url;
    
    // ALWAYS ensure video starts muted before soundEnabled
    if (!soundEnabled) {
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
    }
  }, [url, soundEnabled, hideControls]);

  // Intersection Observer for viewport detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Play/Pause based on visibility (always muted autoplay initially)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || videoError || !url) return;

    if (isInView) {
      // Ensure muted state is correct before autoplay
      if (!soundEnabled) {
        video.muted = true;
      }
      video.play().catch((err) => {
        console.log('[RE-HERO] Autoplay blocked:', err.message);
      });
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [isInView, videoError, url, soundEnabled]);

  // Update duration when video metadata loads
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleLoadedMetadata = () => {
      setDuration(video.duration || 0);
    };
    
    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(video.currentTime);
      }
    };
    
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    
    // If metadata already loaded
    if (video.duration) {
      setDuration(video.duration);
    }
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [isSeeking, url]);

  /**
   * Handle "Tap for sound" button click
   * Restarts video from beginning with audio, then shows custom controls
   */
  const handleTapForSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    console.log('[RE-HERO] Tap for sound - restarting from 0:00 with audio');
    
    // Step 1: Pause first
    video.pause();
    
    // Step 2: Reset to beginning
    video.currentTime = 0;
    setCurrentTime(0);
    
    // Step 3: Unmute and set volume
    video.muted = false;
    video.volume = 1;
    
    // Step 4: Play with error handling and retry on next animation frame
    video.play().catch((err) => {
      console.log('[RE-HERO] Play blocked, retrying on next frame:', err.message);
      requestAnimationFrame(() => {
        video.play().catch(() => {});
      });
    });
    
    // Step 5: Update state - enable sound and show controls with auto-hide timer
    setSoundEnabled(true);
    setIsPlaying(true);
    showControls();
  }, [showControls]);

  /**
   * Toggle play/pause - also shows controls and resets hide timer
   */
  const handlePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Show controls and reset timer on interaction
    showControls();
    
    if (video.paused) {
      video.play().catch(() => {});
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, [showControls]);

  /**
   * Handle scrubber change (seeking) - also shows controls and resets hide timer
   */
  const handleSeekStart = useCallback(() => {
    setIsSeeking(true);
    showControls();
  }, [showControls]);

  const handleSeekChange = useCallback((e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    // Reset timer on continuous interaction
    showControls();
  }, [showControls]);

  const handleSeekEnd = useCallback((e) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
    setIsSeeking(false);
    
    // Continue playing if it was playing before
    if (isPlaying) {
      video.play().catch(() => {});
    }
    
    // Reset timer after seek ends
    showControls();
  }, [isPlaying, showControls]);

  /**
   * Handle tap/click on video area (when controls are hidden) to show controls
   */
  const handleVideoAreaClick = useCallback(() => {
    if (soundEnabled && !controlsVisible) {
      console.log('[RE-HERO] Video area tapped - showing controls');
      showControls();
    }
  }, [soundEnabled, controlsVisible, showControls]);

  /**
   * Format time as MM:SS
   */
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle video errors
  const handleVideoError = () => {
    console.error('[RE-HERO] Video playback error');
    setVideoError(true);
  };

  // Loading state
  if (loading) {
    return (
      <div ref={containerRef} className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl animate-pulse"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black aspect-video flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
            <span className="text-zinc-400 text-sm">Loading video...</span>
          </div>
        </div>
      </div>
    );
  }

  // No video configured
  if (!hasVideo && !loading) {
    return (
      <div ref={containerRef} className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Property Video Coming Soon</p>
            <p className="text-sm mt-1">Check back shortly</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state with retry
  if ((error || videoError) && !loading) {
    return (
      <div ref={containerRef} className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Video Unavailable</p>
            <p className="text-sm mt-1">Please try again later</p>
            <button 
              onClick={retry}
              className="mt-4 px-4 py-2 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg flex items-center gap-2 mx-auto transition"
            >
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Glow Effect */}
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-60"></div>
      
      {/* Video Container */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black">
        {/* CSS to hide ALL browser native controls */}
        <style>{`
          #re-hero-video::-webkit-media-controls,
          #re-hero-video::-webkit-media-controls-enclosure,
          #re-hero-video::-webkit-media-controls-panel,
          #re-hero-video::-webkit-media-controls-play-button,
          #re-hero-video::-webkit-media-controls-timeline,
          #re-hero-video::-webkit-media-controls-current-time-display,
          #re-hero-video::-webkit-media-controls-time-remaining-display,
          #re-hero-video::-webkit-media-controls-mute-button,
          #re-hero-video::-webkit-media-controls-volume-slider,
          #re-hero-video::-webkit-media-controls-fullscreen-button {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
          #re-hero-video::-moz-media-controls {
            display: none !important;
          }
          /* Custom scrubber styling */
          .hero-scrubber {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 6px;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 3px;
            cursor: pointer;
          }
          .hero-scrubber::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 14px;
            height: 14px;
            background: #10b981;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          .hero-scrubber::-moz-range-thumb {
            width: 14px;
            height: 14px;
            background: #10b981;
            border-radius: 50%;
            cursor: pointer;
            border: none;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          }
          .hero-scrubber::-webkit-slider-runnable-track {
            height: 6px;
            border-radius: 3px;
          }
          .hero-scrubber::-moz-range-track {
            height: 6px;
            border-radius: 3px;
            background: rgba(255, 255, 255, 0.2);
          }
        `}</style>
        
        {/* Clickable overlay for re-showing controls when hidden */}
        {soundEnabled && !controlsVisible && (
          <div 
            className="absolute inset-0 z-10 cursor-pointer"
            onClick={handleVideoAreaClick}
            onTouchStart={handleVideoAreaClick}
          />
        )}
        
        <video
          id="re-hero-video"
          ref={videoRef}
          src={url}
          poster={posterUrl}
          className={`w-full aspect-video object-cover select-none ${!soundEnabled ? 'pointer-events-none' : ''}`}
          style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          autoPlay
          loop
          muted={!soundEnabled}
          playsInline
          preload="auto"
          onError={handleVideoError}
          tabIndex={-1}
          data-testid="re-hero-video"
        />
        
        {/* Tap for Sound Button - Only shows before sound is enabled */}
        {!soundEnabled && (
          <button
            onClick={handleTapForSound}
            className="absolute top-4 right-4 z-10 px-4 py-3 bg-black/70 hover:bg-black/90 rounded-xl backdrop-blur-sm flex items-center gap-2 border border-white/20 transition-all duration-300"
            title="Tap for sound"
            data-testid="hero-sound-toggle"
          >
            <VolumeX className="w-6 h-6 text-white" />
            <span className="text-white font-medium text-sm">Tap for sound</span>
          </button>
        )}
        
        {/* Custom Controls - Shows after Tap for sound, auto-hides after 3s */}
        <div 
          className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-8 transition-opacity duration-300 ${
            soundEnabled && controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <div className="flex items-center gap-3">
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              title={isPlaying ? 'Pause' : 'Play'}
              data-testid="hero-play-pause"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" fill="white" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
              )}
            </button>
            
            {/* Time Display */}
            <span className="text-white/80 text-xs font-mono min-w-[45px]">
              {formatTime(currentTime)}
            </span>
            
            {/* Scrubber/Timeline */}
            <input
              type="range"
              min="0"
              max={duration || 100}
              step="0.1"
              value={currentTime}
              onChange={handleSeekChange}
              onMouseDown={handleSeekStart}
              onTouchStart={handleSeekStart}
              onMouseUp={handleSeekEnd}
              onTouchEnd={handleSeekEnd}
              className="hero-scrubber flex-1"
              style={{
                background: `linear-gradient(to right, #10b981 ${(currentTime / (duration || 1)) * 100}%, rgba(255,255,255,0.2) ${(currentTime / (duration || 1)) * 100}%)`
              }}
              data-testid="hero-scrubber"
            />
            
            {/* Duration Display */}
            <span className="text-white/80 text-xs font-mono min-w-[45px] text-right">
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};


/**
 * Demo Video Player for Real Estate page (Bottom Video)
 * - Silent autoplay when scrolls into view
 * - Loops continuously
 * - No controls, no sound, no UI elements
 * - No user interaction required or allowed
 * - Does NOT restart on repeated scrolls
 */
export const REDemoVideoPlayer = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  // Track if video has started playing (don't restart on repeated scrolls)
  const hasStartedRef = useRef(false);
  
  // Fetch playback URL
  const { url, posterUrl, hasVideo, loading, error, retry } = usePlaybackUrl('demo');

  // Intersection Observer for viewport detection - start playing when visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url || videoError) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start playing when in view (don't restart if already playing)
            if (video.paused) {
              video.play().catch((err) => {
                console.log('[RE-DEMO] Autoplay blocked:', err.message);
              });
            }
            hasStartedRef.current = true;
          }
          // Note: We do NOT pause when leaving view - video continues looping
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [url, videoError]);

  // Ensure video stays muted and looping
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
  }, [url]);

  // Handle video errors
  const handleVideoError = () => {
    console.error('[RE-DEMO] Video playback error');
    setVideoError(true);
  };

  // Prevent any click/tap from affecting playback
  const preventInteraction = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Loading state - minimal, no spinner
  if (loading) {
    return (
      <div ref={containerRef} className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-40"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black aspect-video">
        </div>
      </div>
    );
  }

  // No video configured
  if (!hasVideo && !loading) {
    return (
      <div ref={containerRef} className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Demo Video Coming Soon</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state with retry
  if ((error || videoError) && !loading) {
    return (
      <div ref={containerRef} className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Video Unavailable</p>
            <button 
              onClick={retry}
              className="mt-4 px-4 py-2 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg flex items-center gap-2 mx-auto transition"
            >
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Glow Effect */}
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-60"></div>
      
      {/* Video Container - No controls, no overlay, no UI */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black">
        <video
          ref={videoRef}
          src={url}
          poster={posterUrl}
          className="w-full aspect-video object-cover pointer-events-none select-none"
          style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onError={handleVideoError}
          onClick={preventInteraction}
          onContextMenu={preventInteraction}
          onTouchStart={preventInteraction}
          tabIndex={-1}
          data-testid="re-demo-video"
        />
      </div>
    </div>
  );
};


export const REMidVideoPlayer = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const hasStartedRef = useRef(false);
  
  const { url, posterUrl, hasVideo, loading, error, retry } = usePlaybackUrl('mid');

  // Intersection observer — autoplay when in view
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url || videoError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && video.paused) {
            video.play().catch(() => {});
            hasStartedRef.current = true;
          }
        });
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [url, videoError]);

  // Keep muted until sound enabled
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !soundEnabled;
    video.loop = true;
    video.playsInline = true;
  }, [url, soundEnabled]);

  // "Tap for sound" — matches hero behavior exactly
  const handleTapForSound = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
    video.muted = false;
    video.volume = 1;
    video.play().catch(() => {
      requestAnimationFrame(() => { video.play().catch(() => {}); });
    });
    setSoundEnabled(true);
  }, []);

  // Toggle mute after sound enabled
  const handleToggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (soundEnabled) {
      video.muted = true;
      setSoundEnabled(false);
    }
  }, [soundEnabled]);

  const handleVideoError = () => setVideoError(true);

  if (loading) return (
    <div ref={containerRef} className="relative">
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-40"></div>
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black aspect-video"></div>
    </div>
  );

  if (!hasVideo && !loading) return (
    <div ref={containerRef} className="relative">
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
        <div className="text-center text-zinc-500">
          <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium">Video Coming Soon</p>
        </div>
      </div>
    </div>
  );

  if ((error || videoError) && !loading) return (
    <div ref={containerRef} className="relative">
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
        <div className="text-center text-zinc-500">
          <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
          <p className="text-lg font-medium">Video Unavailable</p>
          <button onClick={retry} className="mt-4 px-4 py-2 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-lg flex items-center gap-2 mx-auto transition">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-60"></div>
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black">
        <video ref={videoRef} src={url} poster={posterUrl}
          className="w-full aspect-video object-cover"
          autoPlay loop muted playsInline preload="auto"
          onError={handleVideoError}
          tabIndex={-1} data-testid="re-mid-video" />
        
        {/* Tap for Sound — same as hero video */}
        {!soundEnabled && (
          <button
            onClick={handleTapForSound}
            className="absolute top-4 right-4 z-10 px-4 py-3 bg-black/70 hover:bg-black/90 rounded-xl backdrop-blur-sm flex items-center gap-2 border border-white/20 transition-all duration-300"
            title="Tap for sound"
            data-testid="mid-sound-toggle"
          >
            <VolumeX className="w-6 h-6 text-white" />
            <span className="text-white font-medium text-sm">Tap for sound</span>
          </button>
        )}

        {/* Mute button — shows after sound enabled */}
        {soundEnabled && (
          <button
            onClick={handleToggleMute}
            className="absolute top-4 right-4 z-10 px-4 py-3 bg-black/70 hover:bg-black/90 rounded-xl backdrop-blur-sm flex items-center gap-2 border border-white/20 transition-all duration-300"
            title="Mute"
            data-testid="mid-mute-toggle"
          >
            <Volume2 className="w-6 h-6 text-white" />
            <span className="text-white font-medium text-sm">Sound on</span>
          </button>
        )}
      </div>
    </div>
  );
};


/**
 * Legacy hook for backwards compatibility
 * @deprecated Use REHeroVideoPlayer and REDemoVideoPlayer components instead
 */
export const useREVideoConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API}/re-videos/config`);
        if (!res.ok) throw new Error('Failed to fetch video config');
        const data = await res.json();
        setConfig(data);
      } catch (err) {
        console.error('Failed to fetch RE video config:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return { config, loading, error };
};

export default { REHeroVideoPlayer, REMidVideoPlayer, REDemoVideoPlayer, useREVideoConfig };
